<template>
  <vs-popup class="ghs-rescan-popup" title="그림문자 일괄 재추출" :active.sync="showPopup">
    <!-- 상단(설명·집계·버튼)은 고정하고 아래 결과 표만 스크롤된다 -->
    <p class="desc">
      그림문자가 하나도 체크되지 않은 MSDS 문서의 첨부 PDF(관련 파일첨부 &gt; MSDS)를 다시 읽어 그림문자를 찾습니다.<br />
      분석 결과를 확인한 뒤 <b>저장</b>을 눌러야 문서에 반영되며, 이미 그림문자가 있는 문서와 PDF 첨부가 없는 문서는 변경하지 않습니다.<br />
      한 번에 분석하는 건수를 제한할 수 있고, 저장 후 <b>다시 분석</b>을 누르면 아직 분석하지 않은 문서부터 다음 회차를 이어갑니다.
    </p>

    <div class="summary-grid">
      <div class="summary-card">
        <span class="summary-label">전체 문서</span>
        <span class="summary-value">{{ summary.total }}</span>
      </div>
      <div class="summary-card">
        <span class="summary-label">그림문자 있음</span>
        <span class="summary-value">{{ summary.hasPictogram }}</span>
      </div>
      <div class="summary-card is-target">
        <span class="summary-label">재추출 대상<br /><small>(그림문자 없음 + PDF 첨부)</small></span>
        <span class="summary-value">{{ summary.target }}</span>
      </div>
      <div class="summary-card">
        <span class="summary-label">PDF 첨부 없음<br /><small>(목록 맨 아래·엑셀에 사유 표시)</small></span>
        <span class="summary-value">{{ summary.noPdf }}</span>
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-4 mt-6 mb-3">
      <div class="status-text">
        <template v-if="loading">문서 목록을 불러오는 중입니다...</template>
        <template v-else-if="analyzing">분석 중... {{ progress.done }} / {{ progress.total }} (이번 회차)</template>
        <template v-else-if="analyzed">{{ stopped ? "분석 중지됨" : "회차 분석 완료" }}: 인식 {{ counts.found }}건, 미인식 {{ counts.notFound }}건<template v-if="counts.notFound > 0">(그림문자 없는 문서 {{ notFoundCounts.declaredNone + notFoundCounts.notClassified + notFoundCounts.signalNone }}, 글자 있음 {{ notFoundCounts.labelOnly }}, 글자 없음 {{ notFoundCounts.noLabel }}, 텍스트 없음·깨짐 {{ notFoundCounts.noText + notFoundCounts.noTextDiamond + notFoundCounts.brokenText + notFoundCounts.brokenTextDiamond }})</template><template v-if="counts.review > 0">, 확인 필요 {{ counts.review }}건</template>, 실패 {{ counts.failed }}건<template v-if="counts.noPdf > 0">, PDF 첨부 없음 {{ counts.noPdf }}건</template><template v-if="counts.pending > 0">, 미분석 {{ counts.pending }}건</template><template v-if="counts.applied > 0">, 저장 완료 {{ counts.applied }}건</template></template>
        <template v-else-if="rows.length > 0">대기 {{ counts.pending }}건<template v-if="counts.noPdf > 0">, PDF 첨부 없음 {{ counts.noPdf }}건(분석 대상 아님, 목록 맨 아래)</template></template>
      </div>
      <div class="ml-auto flex flex-wrap gap-4">
        <vs-button color="secondary" type="border" :disabled="analyzing || saving" @click="cancel">닫기</vs-button>
        <vs-button color="secondary" type="border" :disabled="!canExport" @click="exportProblemRows">미인식·실패 엑셀 ({{ counts.notFound + counts.review + counts.failed + counts.noPdf }}건)</vs-button>
        <vs-button v-if="analyzing" color="danger" type="border" :disabled="stopRequested" @click="requestStop">{{ stopRequested ? "중지 중..." : "분석 중지" }}</vs-button>
        <vs-button v-else color="secondary" type="border" :disabled="loading || saving || remainingCount === 0" @click="startAnalyze">{{ analyzed ? "다시 분석" : "분석 시작" }} ({{ nextBatchCount }}건)</vs-button>
        <vs-button color="primary" :disabled="!canSave" @click="confirmSave">저장 ({{ counts.savable }}건)</vs-button>
      </div>
    </div>

    <div class="flex flex-wrap items-center justify-end gap-6 mb-2">
      <div class="flex items-center gap-2 option-text">
        <span>한 번에 분석할 최대 건수</span>
        <vs-select v-model="batchLimit" class="batch-select" :disabled="analyzing">
          <vs-select-item v-for="n in batchLimitOptions" :key="n" :value="n" :text="`${n}건`" />
        </vs-select>
      </div>
      <vs-checkbox v-model="followCurrent" class="follow-checkbox">분석 중인 항목 따라가기</vs-checkbox>
    </div>

    <div ref="scrollBox" class="result-scroll">
      <table class="result-table">
        <colgroup>
          <col width="50" />
          <col />
          <col width="120" />
          <col width="90" />
          <col width="260" />
          <col width="70" />
        </colgroup>
        <thead>
          <tr>
            <th>No</th>
            <th>제품명</th>
            <th>등록사업장</th>
            <th>상태</th>
            <th>인식된 그림문자</th>
            <th>신호어</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="6" class="empty-cell">문서 목록을 불러오는 중입니다.</td>
          </tr>
          <tr v-else-if="rows.length === 0">
            <td colspan="6" class="empty-cell">재추출 대상 문서가 없습니다.</td>
          </tr>
          <tr v-else v-for="(row, index) in rows" :key="row.postId" :data-post-id="row.postId" :class="`status-${row.status}`">
            <td>{{ index + 1 }}</td>
            <td class="text-left">
              {{ row.제품명 || "-" }}
              <div v-if="row.error" class="error-text">{{ row.error }}</div>
              <div v-else-if="row.reason" class="reason-text">{{ row.reason }}</div>
            </td>
            <td>{{ row.등록사업장 || "-" }}</td>
            <td>{{ statusLabel(row.status) }}</td>
            <td class="text-left">{{ row.foundLabels.length ? row.foundLabels.join(", ") : "-" }}</td>
            <td>{{ row.status === "found" || row.status === "applied" ? row.신호어 : "-" }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </vs-popup>
</template>

<script>
import XLSX from "xlsx-js-style";
import config from "../../../../../public/config";
import * as DataUtil from "@/util/DataUtil";
import { GHS_PICTOGRAM_LIST } from "./ghsConstants";
import { extractPictogramsFromPdf } from "./msdsPdfParser";

const BOARD_ID = 1030;
// PDF 를 받아 페이지 전체 이미지를 비교하는 작업이라 한 건이 수 초 걸릴 수 있다. 서버/브라우저 부담을 줄이기 위해 동시 처리 수를 제한한다.
const CONCURRENCY = 2;
// 그림문자는 2절(유해성·위험성)에 있어 문서 앞부분에만 나온다. 수십 페이지짜리 MSDS 의 뒷부분 이미지까지 훑지 않도록 제한한다.
const MAX_SCAN_PAGES = 8;
// 이미지 해시 계산은 메인 스레드에서 동기로 돌아, 문서 사이에 잠깐 쉬어 주지 않으면 화면 갱신이 밀리고 브라우저가 "응답 없음" 으로 보인다.
const YIELD_MS = 30;
// 한 회차에 분석하는 최대 건수. 문서가 늘어도 한 번에 전부 돌리지 않고 회차를 나눠 메모리·시간을 묶어 둔다(실측 약 2.4건/초).
const BATCH_LIMIT_OPTIONS = [100, 200, 300, 500, 1000];
const DEFAULT_BATCH_LIMIT = 300;

const yieldToBrowser = () => new Promise((resolve) => setTimeout(resolve, YIELD_MS));

const STATUS_LABELS = {
  pending: "대기",
  analyzing: "분석 중",
  found: "인식됨",
  notFound: "미인식",
  review: "확인 필요",
  failed: "실패",
  applied: "저장 완료",
  noPdf: "PDF 첨부 없음",
};

// "다시 분석" 때 집어 드는 순서: 아직 안 본 문서 → 미인식 → 실패. 인식됨/저장 완료는 다시 돌리지 않는다.
const RETRY_PRIORITY = { pending: 0, notFound: 1, review: 1, failed: 2 };
// 미인식 사유. 샘플을 모아 보완할 때 어느 쪽인지 구분하기 위한 분류다.
const NOT_FOUND_REASONS = {
  declaredNone: "그림문자 항목에 '해당없음/자료없음' 표기(그림문자 없는 문서)",
  notClassified: "GHS 분류되지 않음 표기(그림문자 없는 문서)",
  signalNone: "신호어 '없음' 표기(그림문자 없는 문서)",
  labelOnly: "'그림문자' 글자는 있으나 그림을 찾지 못함",
  noLabel: "'그림문자' 글자 없음(경고표지 항목이 없거나 서식이 다름)",
  noText: "텍스트 없음(스캔 이미지 PDF) — 빨간/검은 마름모도 없어 그림문자 없는 문서로 추정(해당없음 표기는 OCR 없이 읽을 수 없음)",
  noTextDiamond: "텍스트 없음(스캔 이미지 PDF) — 마름모는 있으나 그림문자를 알아보지 못함(직접 확인 필요)",
  brokenText: "텍스트 깨짐(글꼴에 유니코드 매핑 없음) — 마름모도 없어 그림문자 없는 문서로 추정(해당없음 표기는 읽을 수 없음)",
  brokenTextDiamond: "텍스트 깨짐(글꼴에 유니코드 매핑 없음) — 마름모는 있으나 그림문자를 알아보지 못함(직접 확인 필요)",
};

function notFoundReason(text, unmatchedPictograms) {
  if (!text) return NOT_FOUND_REASONS.labelOnly;
  if (text.declaredNone) return NOT_FOUND_REASONS.declaredNone;
  if (text.notClassified) return NOT_FOUND_REASONS.notClassified;
  if (text.signalNone) return NOT_FOUND_REASONS.signalNone;
  if (text.hasPictogramLabel) return NOT_FOUND_REASONS.labelOnly;
  if (text.length < 50) return unmatchedPictograms > 0 ? NOT_FOUND_REASONS.noTextDiamond : NOT_FOUND_REASONS.noText;
  if (text.brokenText) return unmatchedPictograms > 0 ? NOT_FOUND_REASONS.brokenTextDiamond : NOT_FOUND_REASONS.brokenText;
  return NOT_FOUND_REASONS.noLabel;
}

function hasAnyPictogram(경고표지요소) {
  const 그림문자 = (경고표지요소 && 경고표지요소.그림문자) || {};
  return Object.values(그림문자).some(Boolean);
}

// PDF 첨부가 없는 문서의 사유. 담당자가 무엇을 다시 올려야 하는지 바로 알 수 있게 첨부 파일 종류를 적는다.
function noPdfReason(body) {
  const file = body && body.MSDS;
  if (!file || typeof file !== "object" || !Object.keys(file).length) return "MSDS 첨부 파일 없음 — PDF 첨부 필요";
  if (!file.path) return "첨부 정보는 있으나 파일 경로가 비어 있음(업로드 미완료 추정) — PDF 다시 첨부 필요";
  const name = file.fileName || file.path || "";
  const ext = (name.match(/\.([a-z0-9]+)$/i) || [])[1];
  return `PDF 가 아닌 ${ext ? ext.toLowerCase() : "다른 형식"} 파일이 첨부됨 — PDF 로 변환해 다시 첨부 필요`;
}

function getPdfPath(body) {
  const file = body && body.MSDS;
  if (!file || !file.path) return "";
  const name = `${file.fileName || ""} ${file.path}`.toLowerCase();
  return name.includes(".pdf") ? file.path : "";
}

export default {
  name: "PopupGHSPictogramRescan",
  props: {
    value: { type: Boolean, default: false },
    projectId: { type: [Number, String], required: true },
  },
  data() {
    return {
      showPopup: this.value,
      loading: false,
      analyzing: false,
      analyzed: false,
      saving: false,
      summary: { total: 0, hasPictogram: 0, target: 0, noPdf: 0 },
      rows: [],
      progress: { done: 0, total: 0 },
      stopRequested: false,
      stopped: false,
      followCurrent: true, // 분석 중인 행이 표 가운데 오도록 자동 스크롤
      batchLimitOptions: BATCH_LIMIT_OPTIONS,
      batchLimit: DEFAULT_BATCH_LIMIT,
    };
  },
  computed: {
    counts() {
      const count = (status) => this.rows.filter((r) => r.status === status).length;
      return {
        pending: count("pending"),
        found: count("found"),
        notFound: count("notFound"),
        review: count("review"),
        failed: count("failed"),
        noPdf: count("noPdf"),
        applied: count("applied"),
        savable: count("found"),
      };
    },
    // 다음 회차에 돌릴 수 있는 문서(인식됨/저장 완료 제외)
    notFoundCounts() {
      const counts = { declaredNone: 0, notClassified: 0, signalNone: 0, labelOnly: 0, noLabel: 0, noText: 0, noTextDiamond: 0, brokenText: 0, brokenTextDiamond: 0 };
      for (const row of this.rows) {
        if (row.status !== "notFound") continue;
        const key = Object.keys(NOT_FOUND_REASONS).find((k) => NOT_FOUND_REASONS[k] === row.reason) || "labelOnly";
        counts[key] += 1;
      }
      return counts;
    },
    remainingRows() {
      return this.rows.filter((r) => r.status in RETRY_PRIORITY).sort((a, b) => RETRY_PRIORITY[a.status] - RETRY_PRIORITY[b.status]);
    },
    remainingCount() {
      return this.remainingRows.length;
    },
    nextBatchCount() {
      return Math.min(this.remainingCount, this.batchLimit);
    },
    canSave() {
      return this.analyzed && !this.analyzing && !this.saving && this.counts.savable > 0;
    },
    canExport() {
      return !this.loading && !this.analyzing && this.counts.notFound + this.counts.review + this.counts.failed + this.counts.noPdf > 0;
    },
  },
  watch: {
    value(value) {
      this.showPopup = value;
      if (value) this.loadPosts();
    },
    showPopup(value) {
      this.$emit("input", value);
    },
  },
  methods: {
    statusLabel(status) {
      return STATUS_LABELS[status] || status;
    },
    cancel() {
      if (this.analyzing || this.saving) return;
      this.showPopup = false;
    },
    async loadPosts() {
      this.loading = true;
      this.analyzed = false;
      this.stopped = false;
      this.rows = [];
      this.summary = { total: 0, hasPictogram: 0, target: 0, noPdf: 0 };
      try {
        // loadDefragmentation(30초)과 LOAD_POST_LIST(5초)는 요청 옵션 JSON 을 키로 캐시한다. 저장 뒤 팝업을 바로
        // 다시 열면 캐시된 옛 목록이 와서 이미 채운 문서가 다시 대상으로 잡히므로, 매번 다른 키가 되도록 시각을 섞는다.
        // (LOAD_POST_LIST 는 아는 파라미터만 서버에 보내므로 cacheBuster 는 요청에 실리지 않는다)
        const posts = await DataUtil.loadDefragmentation(this.$store, "board/LOAD_POST_LIST", {
          projectId: this.projectId,
          boardId: BOARD_ID,
          page: 1,
          perPage: 1000,
          cacheBuster: Date.now(),
        });

        const rows = [];
        const noPdfRows = []; // PDF 첨부 없음: 분석하지 않지만 목록·엑셀에서 사유를 보여 준다
        const summary = { total: 0, hasPictogram: 0, target: 0, noPdf: 0 };
        for (const post of posts) {
          if (!post.post_content) continue;
          let content = null;
          try {
            content = JSON.parse(post.post_content);
          } catch (e) {
            continue;
          }
          const body = (content && content.body) || {};
          summary.total++;

          if (hasAnyPictogram(body.경고표지요소)) {
            summary.hasPictogram++;
            continue;
          }
          const pdfPath = getPdfPath(body);
          if (!pdfPath) {
            summary.noPdf++;
            noPdfRows.push({
              postId: post.post_id || post.id,
              post: Object.freeze(post),
              content: Object.freeze(content),
              제품명: body.제품명 || "",
              등록사업장: (body.stdInfo && body.stdInfo.field_name) || "",
              pdfPath: "",
              fileName: (body.MSDS && body.MSDS.fileName) || "",
              status: "noPdf",
              found: {},
              foundLabels: [],
              신호어: "",
              error: "",
              reason: noPdfReason(body),
            });
            continue;
          }
          summary.target++;
          // 문서 원본(post/content)은 저장 때 읽기만 하므로 반응형에서 제외한다. 1,000건이 넘는 본문 전체를
          // Vue 가 observer 로 감싸면 메모리와 CPU 를 크게 먹어 팝업이 느려진다.
          rows.push({
            postId: post.post_id || post.id,
            post: Object.freeze(post),
            content: Object.freeze(content),
            제품명: body.제품명 || "",
            등록사업장: (body.stdInfo && body.stdInfo.field_name) || "",
            pdfPath,
            fileName: (body.MSDS && body.MSDS.fileName) || "",
            status: "pending",
            found: {},
            foundLabels: [],
            신호어: "",
            error: "",
            reason: "",
          });
        }
        this.rows = rows.concat(noPdfRows);
        this.summary = summary;
      } catch (err) {
        this.$vs.notify({ title: "목록 조회 실패", text: (err && err.message) || "잠시 후 다시 시도해주세요.", color: "danger" });
      } finally {
        this.loading = false;
      }
    },
    async downloadPdf(row) {
      const hashedName = row.pdfPath.split("/").pop();
      const result = await this.$store.dispatch("file/FILE_DOWNLOAD", { hashedName });
      const blob = result instanceof Blob ? result : result && result.data;
      if (!(blob instanceof Blob)) throw new Error("PDF 파일을 내려받지 못했습니다.");
      await this.assertPdfHeader(blob);
      return blob;
    },
    // pdf.js 의 "Invalid PDF structure." 만으로는 원인을 알 수 없어, 파일 머리글로 먼저 걸러 이유를 표시한다.
    // 실측: 본서버 실패 파일은 모두 "<DOCUMENT SAFER" 로 시작하는 문서보안(DRM) 래핑 파일이었다(브라우저에서 해제 불가).
    // PDF 규격상 %PDF 머리글 앞에 최대 1024바이트 잡데이터가 허용되므로 그 범위에서 찾는다.
    async assertPdfHeader(blob) {
      const head = new Uint8Array(await blob.slice(0, 1024).arrayBuffer());
      let text = "";
      for (let i = 0; i < head.length; i++) text += String.fromCharCode(head[i]);
      if (text.includes("%PDF")) return;
      if (/^<DOCUMENT\s*SAFER/i.test(text)) throw new Error("문서보안(DRM) 적용 파일이라 읽을 수 없습니다. DRM 해제된 PDF를 다시 첨부해주세요.");
      if (blob.size === 0) throw new Error("첨부 파일이 비어 있습니다(0바이트).");
      throw new Error("첨부 파일이 PDF 형식이 아닙니다. 올바른 PDF를 다시 첨부해주세요.");
    },
    async analyzeRow(row) {
      row.status = "analyzing";
      row.error = "";
      if (this.followCurrent) this.scrollToRow(row.postId);
      try {
        const blob = await this.downloadPdf(row);
        const result = await extractPictogramsFromPdf(blob, { maxPages: MAX_SCAN_PAGES });
        const found = result.그림문자 || {};
        row.found = found;
        row.foundLabels = GHS_PICTOGRAM_LIST.filter((p) => found[p.key]).map((p) => p.label);
        row.신호어 = result.신호어 || "없음";
        row.status = row.foundLabels.length > 0 ? "found" : "notFound";
        row.reason = row.status === "notFound" ? notFoundReason(result.text, result.unmatchedPictograms) : "";
        // 일부만 인식했거나 신호어가 둘 다 적힌 문서는 자동 저장하면 틀린 값이 들어가므로 "확인 필요" 로 빼서 저장 대상에서 제외한다
        const problems = [];
        if (row.status === "found" && result.unmatchedPictograms > 0) {
          problems.push(`그림문자 ${row.foundLabels.length + result.unmatchedPictograms}개 중 ${row.foundLabels.length}개만 인식`);
        }
        if (result.text && result.text.signalAmbiguous) problems.push("신호어가 '위험'과 '경고' 둘 다 표기됨");
        // 신호어를 못 읽은 채(스캔본 등) "없음"으로 저장하면 틀린 값이 들어가므로 확인 필요로 뺀다.
        // 문서에 "없음/해당없음"이 실제로 적혀 있어 읽은 경우(text.signalNone)는 그대로 저장해도 된다.
        if (row.status === "found" && row.신호어 === "없음" && !(result.text && result.text.signalNone)) {
          problems.push("신호어를 읽지 못함");
        }
        if (problems.length) {
          row.status = "review";
          row.error = `${problems.join(", ")} — 저장 제외, 문서에서 직접 확인·입력 필요`;
        }
      } catch (err) {
        row.status = "failed";
        row.error = (err && err.message) || "PDF 인식 실패";
      }
    },
    // 분석 중인 행을 표 스크롤 영역의 가운데로 옮긴다. 페이지가 아니라 표 안쪽 스크롤만 움직인다.
    scrollToRow(postId) {
      this.$nextTick(() => {
        const box = this.$refs.scrollBox;
        const el = box && box.querySelector(`[data-post-id="${postId}"]`);
        if (!box || !el) return;
        const delta = el.getBoundingClientRect().top - box.getBoundingClientRect().top;
        box.scrollTop += delta - (box.clientHeight / 2) + (el.clientHeight / 2);
      });
    },
    requestStop() {
      this.stopRequested = true;
    },
    async startAnalyze() {
      if (this.analyzing || this.remainingCount === 0) return;
      // 이번 회차 대상: 대기 → 미인식 → 실패 순으로 batchLimit 건까지. 인식됨/저장 완료 행은 그대로 둔다.
      const batch = this.remainingRows.slice(0, this.batchLimit);
      this.analyzing = true;
      this.analyzed = false;
      this.stopRequested = false;
      this.stopped = false;
      this.progress = { done: 0, total: batch.length };
      for (const row of batch) {
        row.status = "pending";
        row.found = {};
        row.foundLabels = [];
        row.신호어 = "";
        row.error = "";
        row.reason = "";
      }

      const queue = batch.slice();
      const worker = async () => {
        while (queue.length > 0 && !this.stopRequested) {
          const row = queue.shift();
          await this.analyzeRow(row);
          this.progress.done++;
          await yieldToBrowser();
        }
      };
      await Promise.all(Array.from({ length: CONCURRENCY }, worker));

      this.stopped = this.stopRequested;
      this.analyzing = false;
      this.analyzed = true;
    },
    // 미인식·실패 문서를 엑셀로 내려받는다. 담당자가 PDF 를 다시 첨부하거나 그림문자를 직접 체크할 때 쓰는 작업 목록이다.
    // 1차로 같은 원인(실패 → 미인식, 같은 상태 안에서는 사유 문구)끼리 묶고, 2차로 표의 문서 순서(No)대로 나열한다.
    // 문서 URL 은 현재 접속 주소가 아니라 config.BASE_URL(본서버 주소)로 만든다 - localhost 에서 뽑아도 바로 쓸 수 있도록.
    exportProblemRows() {
      if (!this.canExport) return;
      const statusOrder = { failed: 0, review: 1, notFound: 2, noPdf: 3 };
      const reasonOf = (row) => {
        if (row.status === "failed" || row.status === "review") return row.error;
        return row.reason || NOT_FOUND_REASONS.labelOnly;
      };
      const targets = this.rows
        .map((row, index) => ({ row, no: index + 1, reason: reasonOf(row) }))
        .filter(({ row }) => row.status in statusOrder)
        .sort((a, b) => statusOrder[a.row.status] - statusOrder[b.row.status] || a.reason.localeCompare(b.reason) || a.no - b.no);

      const header = ["No", "문서ID", "제품명", "등록사업장", "상태", "사유", "첨부 파일명", "문서 URL"];
      const data = [header];
      for (const { row, no, reason } of targets) {
        data.push([no, row.postId, row.제품명 || "", row.등록사업장 || "", this.statusLabel(row.status), reason, row.fileName || "", `${config.BASE_URL}/project/${this.projectId}/tab/${BOARD_ID}/write/${row.postId}`]);
      }

      const ws = XLSX.utils.aoa_to_sheet(data);
      ws["!cols"] = [{ wch: 6 }, { wch: 9 }, { wch: 36 }, { wch: 12 }, { wch: 9 }, { wch: 52 }, { wch: 40 }, { wch: 60 }];
      const urlCol = header.indexOf("문서 URL");
      const border = { style: "thin", color: { rgb: "CCCCCC" } };
      const range = XLSX.utils.decode_range(ws["!ref"]);
      for (let r = range.s.r; r <= range.e.r; r++) {
        for (let c = range.s.c; c <= range.e.c; c++) {
          const addr = XLSX.utils.encode_cell({ r, c });
          if (!ws[addr]) ws[addr] = { t: "s", v: "" };
          const isLink = r > 0 && c === urlCol && ws[addr].v;
          if (isLink) ws[addr].l = { Target: ws[addr].v, Tooltip: "문서 열기" };
          ws[addr].s = {
            alignment: { vertical: "center", wrapText: true, horizontal: r === 0 ? "center" : "left" },
            border: { top: border, bottom: border, left: border, right: border },
            ...(r === 0 ? { font: { bold: true }, fill: { fgColor: { rgb: "F2F2F2" } } } : {}),
            ...(isLink ? { font: { color: { rgb: "0563C1" }, underline: true } } : {}),
          };
        }
      }
      // 머리글 행에 자동 필터(사유·상태·등록사업장으로 걸러 보기 편하도록)
      ws["!autofilter"] = { ref: ws["!ref"] };
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "미인식·실패");
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      XLSX.writeFile(wb, `MSDS_그림문자_재추출_미인식실패_${stamp}.xlsx`);
    },
    confirmSave() {
      if (!this.canSave) return;
      this.$vs.dialog({
        type: "confirm",
        title: "그림문자 저장",
        text: `인식된 ${this.counts.savable}건의 문서에 그림문자를 저장하시겠습니까? (신호어는 비어 있는 문서에만 채워집니다)`,
        acceptText: "저장",
        cancelText: "취소",
        accept: () => this.saveAll(),
      });
    },
    async saveAll() {
      if (this.saving) return;
      this.saving = true;
      let success = 0;
      let failed = 0;
      for (const row of this.rows) {
        if (row.status !== "found") continue;
        try {
          await this.saveRow(row);
          row.status = "applied";
          success++;
        } catch (err) {
          row.status = "failed";
          row.error = (err && err.response && err.response.data && (err.response.data.message || err.response.data)) || (err && err.message) || "저장 실패";
          if (typeof row.error !== "string") row.error = "저장 실패";
          failed++;
        }
      }
      this.saving = false;
      this.$vs.notify({
        title: "그림문자 저장",
        text: `${success}건 저장${failed > 0 ? `, ${failed}건 실패` : ""}`,
        color: failed > 0 ? "warning" : "success",
      });
      if (success > 0) this.$emit("saved", success);
    },
    async saveRow(row) {
      // 문서 본문(post_content)의 경고표지요소.그림문자만 바꾸고 나머지는 그대로 둔다
      const content = JSON.parse(JSON.stringify(row.content));
      const body = content.body || (content.body = {});
      const 경고표지요소 = body.경고표지요소 || (body.경고표지요소 = {});
      경고표지요소.그림문자 = { ...(경고표지요소.그림문자 || {}), ...row.found };
      if ((!경고표지요소.신호어 || 경고표지요소.신호어 === "없음") && row.신호어 && row.신호어 !== "없음") {
        경고표지요소.신호어 = row.신호어;
      }

      const post = row.post;
      const title = post.post_title !== undefined ? post.post_title : post.title;
      await this.$store.dispatch("board/UPDATE_POST", {
        projectId: this.projectId,
        boardId: BOARD_ID,
        postId: row.postId,
        title: title === null ? undefined : title,
        content: JSON.stringify(content),
      });
      row.content = Object.freeze(content);
    },
  },
};
</script>

<style scoped lang="scss">
.ghs-rescan-popup::v-deep {
  .vs-popup {
    width: 95%;
    @media (min-width: 1200px) {
      width: 1100px;
    }
  }
}

.desc {
  font-size: 14px;
  line-height: 1.6;
  color: #555;
  margin-bottom: 16px;

  b {
    font-weight: 700;
    color: #333;
  }
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.summary-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px 8px;
  border: 1px solid #cdcdcd;
  border-radius: 6px;
  background: #fafafa;
  text-align: center;

  &.is-target {
    border-color: #7e72f2;
    background: #f0f0fd;
  }

  .summary-label {
    font-size: 13px;
    color: #555;
    line-height: 1.3;

    small {
      font-size: 11px;
      color: #888;
    }
  }

  .summary-value {
    font-size: 22px;
    font-weight: 700;
  }
}

.status-text {
  font-size: 14px;
  font-weight: 500;
}

.option-text {
  font-size: 13px;
}

.batch-select {
  width: 110px;
}

.follow-checkbox::v-deep {
  font-size: 13px;

  .vs-checkbox {
    margin-right: 6px;
  }
}

// 상단은 고정하고 표만 스크롤. 팝업 자체가 화면을 넘지 않도록 높이를 화면 기준으로 잡는다(위쪽 영역 약 470px 제외).
.result-scroll {
  position: relative;
  max-height: calc(100vh - 470px);
  min-height: 200px;
  overflow: auto;
  border: 1px solid #cdcdcd;
}

.result-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;

  th,
  td {
    font-size: 13px;
    text-align: center;
    border-right: 1px solid #cdcdcd;
    border-bottom: 1px solid #cdcdcd;
    padding: 8px;
    word-break: break-all;

    &:last-child {
      border-right: none;
    }
  }

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    font-weight: 700;
    background-color: #f9f9f9;
  }

  td.text-left {
    text-align: left;
  }

  .empty-cell {
    padding: 32px 0;
    color: #888;
  }

  .error-text {
    font-size: 12px;
    color: #ea5455;
    margin-top: 4px;
  }
  .reason-text {
    font-size: 12px;
    color: #888;
    margin-top: 4px;
  }

  tr.status-found td {
    background: #f3fbf4;
  }
  tr.status-applied td {
    background: #e8f5e9;
  }
  tr.status-failed td {
    background: #fff4f4;
  }
  tr.status-review td {
    background: #fff8e6;
  }
  tr.status-noPdf td {
    background: #f5f5f5;
    color: #777;
  }
  tr.status-analyzing td {
    background: #f0f0fd;
  }
}
</style>
